import { useState } from "react"

// "plaintext-only" makes pasted text lose its formatting. Older browsers
// (Firefox before 136) do not know the value and would not make the block
// editable at all, so they fall back to ordinary editing.
let plaintextOnlySupported: boolean | null = null

function supportsPlaintextOnly() {
  if (plaintextOnlySupported === null) {
    const probe = document.createElement("div")
    try {
      probe.contentEditable = "plaintext-only"
    } catch {
      // unsupported value: the property throws in some browsers
    }
    plaintextOnlySupported = probe.contentEditable === "plaintext-only"
  }

  return plaintextOnlySupported
}

const LINE_BREAK = String.fromCharCode(10)

// The text of an edited block with its line breaks. textContent would drop
// them (a <br> or the end of a <div> has no text), and innerText turns an
// empty <div><br></div> line into two line breaks. Browsers differ in what
// Enter inserts (a line break in the text, a <br>, or a <div>), so all three
// are read here: one Enter is one line break.
export function readEditedText(root: HTMLElement) {
  let text = ""

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.nodeValue || ""
      return
    }

    if (!(node instanceof HTMLElement)) return

    if (node.tagName === "BR") {
      text += LINE_BREAK
      return
    }

    // A block starts on a new line, unless the text already ends with one
    const isBlock = node.tagName === "DIV" || node.tagName === "P"
    if (isBlock && text !== "" && !text.endsWith(LINE_BREAK)) text += LINE_BREAK

    node.childNodes.forEach(walk)
  }

  root.childNodes.forEach(walk)

  return text.replace(/\r\n/g, LINE_BREAK).replace(/\n+$/, "")
}

// Click-to-edit text block for the site-content pilot (SCRUM: Home page
// admin editing). Renders as plain text normally; when the page's edit
// mode is on (admin only), it becomes an editable field that saves on
// blur, similar to editing a block directly on a WordPress page.
//
// A multiline block keeps its line breaks both while editing and when the
// page is shown to visitors. A single-line block ignores Enter.
export default function EditableText({
  value,
  editMode,
  onSave,
  as: Tag = "span",
  multiline = false,
  className = ""
}: {
  value: string
  editMode: boolean
  onSave: (nextValue: string) => Promise<void> | void
  as?: "span" | "p" | "h1" | "h2" | "h3"
  multiline?: boolean
  className?: string
}) {
  const [saving, setSaving] = useState(false)

  if (!editMode) {
    return (
      <Tag
        className={`${className} break-words`}
        style={multiline ? { whiteSpace: "pre-wrap" } : undefined}
      >
        {value}
      </Tag>
    )
  }

  async function handleBlur(event: React.FocusEvent<HTMLElement>) {
    const nextValue = readEditedText(event.currentTarget)
    if (nextValue === value) return

    setSaving(true)
    try {
      await onSave(nextValue)
    } finally {
      setSaving(false)
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (!multiline && event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
    }
  }

  return (
    <Tag
      // A new key after each save puts the saved text back into a clean
      // element, so the edited DOM never drifts from what React renders.
      key={value}
      className={`${className} break-words rounded outline-dashed outline-1 outline-offset-2 outline-blue-400 ${
        saving ? "opacity-50" : ""
      }`}
      contentEditable={multiline && supportsPlaintextOnly() ? "plaintext-only" : true}
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        whiteSpace: multiline ? "pre-wrap" : "normal",
        overflowWrap: "anywhere"
      }}
    >
      {value}
    </Tag>
  )
}
